import type { Asset } from '@/types';

/**
 * Subtype keys returned by the graph `/my` endpoint when the result type
 * is logically "asset". The wire format splits items into 10 buckets;
 * callers virtually always want the union flattened into a single Asset[].
 *
 * Keep in sync with the `case 'asset'` branch of `handleGraphQueryResponse`
 * in `useMy.ts`. If a new asset subtype is added on the backend, it must
 * be added here too.
 */
export const ASSET_SUBTYPE_KEYS = [
  'assets',
  'adobjects',
  'addomains',
  'generics',
  'integrations',
  'repositories',
  'webapplications',
  'awsresources',
  'azureresources',
  'gcpresources',
] as const;

type AssetGraphResponse = Partial<
  Record<(typeof ASSET_SUBTYPE_KEYS)[number], Asset[] | undefined>
>;

/**
 * Flatten a graph `/my?label=asset` response into a single Asset[].
 * Tolerant of missing keys, null, and non-array values (treats them as []).
 */
export function flattenAssetGraphResponse(data: unknown): Asset[] {
  if (!data || typeof data !== 'object') return [];
  const d = data as AssetGraphResponse;
  return ASSET_SUBTYPE_KEYS.flatMap(key => {
    const value = d[key];
    return Array.isArray(value) ? value : [];
  });
}
